import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Light, minimal marketing navigation used on all public marketing pages.
 * Transparent on top of page, then adds a subtle border + solid background on scroll.
 */
const MarketingNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      setIsAuthenticated(true);
    } catch (_e) {
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 16);
    handler();
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const linkClass = (path) => {
    const active = location.pathname === path;
    return `text-sm font-medium transition-colors ${active ? 'text-[#0A0A0A]' : 'text-[#525252] hover:text-[#0A0A0A]'}`;
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all ${scrolled ? 'bg-[#F5F1EA]/90 backdrop-blur-xl border-b border-[#0A0A0A]/10' : 'bg-transparent'}`}
      data-testid="marketing-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <div className="w-9 h-9 rounded-full bg-[#0A0A0A] flex items-center justify-center">
            <span className="text-[#B8935A] font-heading font-bold text-lg">S</span>
          </div>
          <span className="font-heading font-bold text-[#0A0A0A] tracking-tight text-lg">Sol Digital</span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <Link to="/" className={linkClass('/')} data-testid="nav-home">Home</Link>
          <Link to="/corporate" className={linkClass('/corporate')} data-testid="nav-teams">For Teams</Link>
          <Link to="/pricing" className={linkClass('/pricing')} data-testid="nav-pricing">Pricing</Link>
          <Link to="/resources" className={linkClass('/resources')} data-testid="nav-resources">Resources</Link>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isAuthenticated ? (
            <Button onClick={() => navigate('/dashboard')} className="bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white rounded-full h-10 px-5 text-sm font-semibold" data-testid="nav-dashboard-button">
              Dashboard
            </Button>
          ) : (
            <>
              <Button onClick={() => navigate('/login')} variant="ghost" className="text-[#0A0A0A] hover:bg-[#0A0A0A]/5 rounded-full h-10 px-4 text-sm font-medium" data-testid="nav-login-button">
                Login
              </Button>
              <Button onClick={() => navigate('/signup')} className="bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white rounded-full h-10 px-5 text-sm font-semibold" data-testid="nav-signup-button">
                Get started
              </Button>
            </>
          )}
        </div>

        <button
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden text-[#0A0A0A] w-10 h-10 flex items-center justify-center"
          data-testid="mobile-menu-button"
        >
          {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {isOpen && (
        <div className="md:hidden bg-[#F5F1EA] border-t border-[#0A0A0A]/10" data-testid="mobile-menu">
          <div className="px-6 py-4 space-y-1">
            <Link to="/" onClick={() => setIsOpen(false)} className="block py-3 text-[#0A0A0A] font-medium">Home</Link>
            <Link to="/corporate" onClick={() => setIsOpen(false)} className="block py-3 text-[#0A0A0A] font-medium">For Teams</Link>
            <Link to="/pricing" onClick={() => setIsOpen(false)} className="block py-3 text-[#0A0A0A] font-medium">Pricing</Link>
            <Link to="/resources" onClick={() => setIsOpen(false)} className="block py-3 text-[#0A0A0A] font-medium">Resources</Link>
            <div className="pt-3 flex flex-col gap-2">
              {isAuthenticated ? (
                <Button onClick={() => { navigate('/dashboard'); setIsOpen(false); }} className="w-full bg-[#0A0A0A] text-white rounded-full h-11">Dashboard</Button>
              ) : (
                <>
                  <Button onClick={() => { navigate('/login'); setIsOpen(false); }} variant="outline" className="w-full border-[#0A0A0A]/20 text-[#0A0A0A] rounded-full h-11 bg-transparent">Login</Button>
                  <Button onClick={() => { navigate('/signup'); setIsOpen(false); }} className="w-full bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white rounded-full h-11">Get started</Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default MarketingNav;
